import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  Search,
  Save,
  Building2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Percent,
} from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";

const SupplierWithTax = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // Existing table pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Editable table (Grid 1) pagination
  const [editPage, setEditPage] = useState(1);
  const [editTotalPages, setEditTotalPages] = useState(1);
  const [editTotalRecords, setEditTotalRecords] = useState(0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSupplierId, setDeleteSupplierId] = useState(null);

  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [supplierCode, setSupplierCode] = useState("");

  const [editableData, setEditableData] = useState([]);
  const [existingData, setExistingData] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

const getBranches = async () => {
    try {
      const res = await axios.get("http://nissi-india.com/api/getAllBranches", {
        headers: commonHeaders,
      });
      const branchList = Array.isArray(res.data.branches) ? res.data.branches : [];
      setBranches(branchList);

      // Auto-select login branch on page load
      if (branchId && branchList.some((b) => String(b.fld_branchid) === String(branchId))) {
        setSelectedBranch(branchId);
      }
    } catch (error) {
      console.error("getBranches error:", error);
      toast.error("Failed to fetch branches");
      setBranches([]);
    }
  };

  const getTaxDetails = async () => {
    try {
      const res = await axios.get("http://nissi-india.com/api/getTaxDetails", {
        headers: commonHeaders,
      });
      const { taxDetails1 = [], taxDetails2 = [], taxDetails3 = [] } = res.data || {};
      const combinedTaxOptions = [...taxDetails1, ...taxDetails2, ...taxDetails3];
      setTaxOptions(combinedTaxOptions);
    } catch (error) {
      console.error("getTaxDetails error:", error);
      toast.error("Failed to fetch tax details");
      setTaxOptions([]);
    }
  };

  const getSuppliers = async (branchIdValue) => {
    try {
      const res = await axios.get(
        `http://nissi-india.com/api/getSuppliersByBranch?branchid=${branchIdValue}`,
        { headers: commonHeaders }
      );
      const data = Array.isArray(res.data.suppliers) ? res.data.suppliers : [];
      setSuppliers(data);
    } catch (error) {
      console.error("getSuppliers error:", error);
      toast.error("Failed to fetch suppliers");
      setSuppliers([]);
    }
  };

  const getEditableData = async (
    branchIdValue,
    supplierIdValue = "",
    supplierCodeValue = "",
    currentPage = 1,
    pageLimit = 10
  ) => {
    try {
      const query = new URLSearchParams({
        branchid: branchIdValue,
        supplierid: supplierIdValue || "",
        suppliercode: supplierCodeValue || "",
        page: currentPage,
        limit: pageLimit,
      }).toString();

      const res = await axios.get(
        `http://nissi-india.com/api/supplierWithTaxBindGrid1?${query}`,
        { headers: commonHeaders }
      );

      const {
        supplierList = [],
        totalRecords = 0,
        totalPages = 1,
      } = res.data || {};

      const normalized = (Array.isArray(supplierList) ? supplierList : []).map((row) => ({
        ...row,
        fld_cgst: row.fld_cgst ?? "",
        fld_sgst: row.fld_sgst ?? "",
        fld_igst: row.fld_igst ?? "",
        nogst: row.nogst ?? false,
        select: row.select ?? false,
      }));

      setEditableData(normalized);
      setEditTotalRecords(totalRecords);
      setEditTotalPages(totalPages || 1);
    } catch (error) {
      console.error("getEditableData error:", error);
      toast.error("Failed to fetch editable data");
      setEditableData([]);
      setEditTotalRecords(0);
      setEditTotalPages(1);
    }
  };

const getExistingData = async (
    branchIdValue,
    supplierIdValue = "",
    supplierCodeValue = "",
    currentPage = 1,
    pageLimit = 10
  ) => {
    try {
      setLoading(true);
      const query = new URLSearchParams({
        branchid: branchIdValue,
        supplierid: supplierIdValue || "",
        suppliercode: supplierCodeValue || "",
        page: currentPage,
        limit: pageLimit,
      }).toString();
      const res = await axios.get(
        `http://nissi-india.com/api/supplierWithExistingTax?${query}`,
        { headers: commonHeaders }
      );

      const { supplierTaxList = [], totalRecords = 0, totalPages = 1 } = res.data || {};
      setExistingData(Array.isArray(supplierTaxList) ? supplierTaxList : []);
      setTotalRecords(totalRecords);
      setTotalPages(totalPages || 1);
    } catch (error) {
      console.error("getExistingData error:", error);
      toast.error("Failed to fetch existing data");
      setExistingData([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
    getBranches();
    getTaxDetails();
  }, [commonHeaders]);

useEffect(() => {
    if (selectedBranch) {
      getSuppliers(selectedBranch);
      getEditableData(selectedBranch, selectedSupplier, supplierCode, editPage, limit);
      getExistingData(selectedBranch, selectedSupplier, supplierCode, page, limit);
    } else {
      setSuppliers([]);
      setEditableData([]);
      setExistingData([]);
      setTotalPages(1);
      setTotalRecords(0);
      setEditTotalPages(1);
      setEditTotalRecords(0);
    }
  }, [selectedBranch, page, editPage, commonHeaders]);

  const handleEditableChange = (id, field, value) => {
    setEditableData((prev) =>
      prev.map((row) =>
        row.supplierid === id
          ? {
              ...row,
              [field]: field === "nogst" || field === "select" ? !row[field] : value,
            }
          : row
      )
    );
  };

  const handleBranchChange = (e) => {
    const value = e.target.value;
    setSelectedBranch(value);
    setSelectedSupplier("");
    setSupplierCode("");
    setPage(1);
    setEditPage(1);
  };

  const handleSearchFilter = async () => {
    if (!selectedBranch) {
      toast.warning("Please select a branch");
      return;
    }
    setPage(1);
    setEditPage(1);
    await getEditableData(selectedBranch, selectedSupplier, supplierCode, 1, limit);
    await getExistingData(selectedBranch, selectedSupplier, supplierCode, 1, limit);
  };

  const handleSubmit = async () => {
    const supplierTaxList = editableData
      .filter((row) => row.select)
      .map((row) => ({
        supplierid: row.supplierid,
        cgst: row.nogst ? 0 : Number(row.fld_cgst) || 0,
        sgst: row.nogst ? 0 : Number(row.fld_sgst) || 0,
        igst: row.nogst ? 0 : Number(row.fld_igst) || 0,
        submit: true,
      }));

    if (!selectedBranch || supplierTaxList.length === 0) {
      toast.warning("Select a branch and at least one supplier to submit");
      return;
    }

    try {
      setSubmitting(true);
     await axios.post(
  "http://nissi-india.com/api/insertSupplierWithTax",
  { branchid: selectedBranch, supplierTaxList },
  { headers: commonHeaders }
);
toast.success("Submitted successfully!");
setPage(1);
await getEditableData(selectedBranch, selectedSupplier, supplierCode, editPage, limit);
await getExistingData(selectedBranch, selectedSupplier, supplierCode, 1, limit);
    } catch (error) {
      console.error("handleSubmit error:", error);
      toast.error(error?.response?.data?.error || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const startRecord = totalRecords === 0 ? 0 : (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, totalRecords);

  const handleRemoveClick = (supplierId) => {
    if (!selectedBranch || !supplierId) return;
    setDeleteSupplierId(supplierId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedBranch || !deleteSupplierId) return;

    try {
      await axios.delete(
        `http://nissi-india.com/api/deleteSupplierWithTax?branchid=${selectedBranch}&supplierid=${deleteSupplierId}`,
        { headers: commonHeaders }
      );

      toast.success("Record removed successfully");

      // Refresh both tables
      await getEditableData(selectedBranch, selectedSupplier, supplierCode, editPage, limit);
      await getExistingData(selectedBranch, selectedSupplier, supplierCode, page, limit);
    } catch (error) {
      console.error("confirmDelete error:", error);
      toast.error(error?.response?.data?.error || "Failed to remove record");
    } finally {
      setShowDeleteConfirm(false);
      setDeleteSupplierId(null);
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Supplier With Tax</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <Percent className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Supplier With Tax Configuration</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Configure CGST / SGST / IGST for suppliers by branch
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className={labelClass}>Branch</label>
              <select
                value={selectedBranch}
                onChange={handleBranchChange}
                className={inputClass}
              >
                <option value="">Select Branch</option>
                {branches.map((branchItem) => (
                  <option key={branchItem.fld_branchid} value={branchItem.fld_branchid}>
                    {branchItem.fld_Branchname || `Branch-${branchItem.fld_branchid}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Supplier</label>
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className={inputClass}
              >
                <option value="">Select Supplier</option>
                {suppliers.map((s) => (
                  <option key={s.fld_supplierid} value={s.fld_supplierid}>
                    {s.fld_suppliername}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Supplier Code</label>
              <input
                type="text"
                placeholder="Enter supplier code"
                className={inputClass}
                value={supplierCode}
                onChange={(e) => setSupplierCode(e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleSearchFilter}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <Search size={16} />
                Search
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Save size={16} />
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>

        {/* Editable Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Configure Tax for Suppliers</h3>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">CGST %</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">SGST %</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">IGST %</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">No GST</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {Array.isArray(editableData) && editableData.length > 0 ? (
                  editableData.map((row, i) => (
                    <tr
                      key={row.supplierid || i}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">{i + 1}</td>
                      <td className="px-4 py-3 font-medium">{row.suppliername}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {row.suppliercode}
                        </span>
                      </td>

                      {["fld_cgst", "fld_sgst", "fld_igst"].map((tax) => (
                        <td className="px-4 py-3" key={tax}>
                          <select
                            value={row.nogst ? "" : row[tax] || ""}
                            onChange={(e) =>
                              handleEditableChange(row.supplierid, tax, e.target.value)
                            }
                            className={`w-full px-2 py-1.5 rounded-lg border text-sm ${
                              isDark
                                ? "bg-slate-800 border-slate-600 text-white"
                                : "bg-white border-slate-300"
                            } ${row.nogst ? "opacity-50 cursor-not-allowed" : ""}`}
                            disabled={row.nogst}
                          >
                            <option value="">0</option>
                            {Array.isArray(taxOptions) &&
                              taxOptions.map((opt, idx) => (
                                <option
                                  key={`${opt.fld_taxpercentid || idx}-${opt.fld_taxpercentage}`}
                                  value={opt.fld_taxpercentage}
                                >
                                  {opt.fld_taxpercentage}
                                </option>
                              ))}
                          </select>
                        </td>
                      ))}

                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={row.nogst || false}
                          onChange={() => handleEditableChange(row.supplierid, "nogst")}
                          className="w-4 h-4 rounded"
                        />
                      </td>

                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={row.select || false}
                          onChange={() => handleEditableChange(row.supplierid, "select")}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      No editable supplier data found. Select a branch and search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Editable Table Pagination + Submit */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing{" "}
              <span className="font-semibold">
                {editTotalRecords === 0 ? 0 : (editPage - 1) * limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold">
                {Math.min(editPage * limit, editTotalRecords)}
              </span>{" "}
              of <span className="font-semibold">{editTotalRecords}</span> results
            </div>

            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setEditPage((prev) => Math.max(prev - 1, 1))}
                  disabled={editPage === 1}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    editPage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                {[...Array(Math.min(editTotalPages, 5))].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setEditPage(idx + 1)}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      editPage === idx + 1
                        ? `bg-${baseColor}-${baseShade} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}

                <button
                  onClick={() => setEditPage((prev) => Math.min(prev + 1, editTotalPages))}
                  disabled={editPage === editTotalPages || editTotalPages === 0}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    editPage === editTotalPages || editTotalPages === 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>

              {editableData.length > 0 && (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  <Save size={16} />
                  {submitting ? "Submitting..." : "Submit Selected"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Existing Data Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Existing Tax Configuration</h3>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">CGST</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">SGST</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">IGST</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">No GST</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Action</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton height={20} baseColor={isDark ? "#374151" : "#f3f4f6"} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : Array.isArray(existingData) && existingData.length > 0 ? (
                  existingData.map((row, i) => {
  const isNoGst =
    Number(row.fld_cgst || 0) +
      Number(row.fld_sgst || 0) +
      Number(row.fld_igst || 0) === 0;

  return (
    <tr
      key={`${row.fld_supplierid}-${row.fld_suppliercode}-${i}`}
      className={`transition ${
        isDark
          ? "bg-gray-800/20 hover:bg-gray-700/30"
          : "bg-white hover:bg-gray-50"
      }`}
    >
      <td className="px-4 py-3 font-medium">
        {(page - 1) * limit + i + 1}
      </td>
      {/* rest of the columns remain the same */}
                        <td className="px-4 py-3 font-medium">{row.fld_suppliername}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {row.fld_suppliercode}
                          </span>
                        </td>
                        <td className="px-4 py-3">{row.fld_cgst}</td>
                        <td className="px-4 py-3">{row.fld_sgst}</td>
                        <td className="px-4 py-3">{row.fld_igst}</td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isNoGst
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {isNoGst ? "Yes" : "No"}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-center">
<button
  onClick={() => handleRemoveClick(row.fld_supplierid)}
  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
>
  Remove
</button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                                            No existing supplier tax data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Showing <span className="font-semibold">{startRecord}</span> to{" "}
                <span className="font-semibold">{endRecord}</span> of{" "}
                <span className="font-semibold">{totalRecords}</span> results
              </div>

              {loading ? (
                <div className="flex space-x-2">
                  <Skeleton width={80} height={36} borderRadius={10} />
                  <Skeleton width={36} height={36} borderRadius={10} />
                  <Skeleton width={36} height={36} borderRadius={10} />
                  <Skeleton width={80} height={36} borderRadius={10} />
                </div>
              ) : (
                <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    <ChevronLeft size={16} />
                    Prev
                  </button>

                  {[...Array(Math.min(totalPages, 5))].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPage(idx + 1)}
                      className={`px-4 py-2 text-sm font-medium transition ${
                        page === idx + 1
                          ? `bg-${baseColor}-${baseShade} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages || totalPages === 0}
                    className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                      page === totalPages || totalPages === 0
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

            <ConfirmModal
        open={showDeleteConfirm}
        title="Remove Tax Configuration"
        message="Are you sure you want to remove this supplier tax configuration? This action cannot be undone."
        confirmLabel="Remove"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteSupplierId(null);
        }}
      />
    </LayoutWrapper>
  );
};

export default SupplierWithTax;